import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  getBudgets, 
  createOrUpdateBudget, 
  copyBudgets,
  getCategories,
  formatCurrency,
  MONTHS,
  TYPE_COLORS
} from '../lib/api';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight, 
  Copy,
  Edit,
  Save
} from 'lucide-react';

const BudgetPage = () => {
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('REDDITO');
  
  // Edit state
  const [editingBudget, setEditingBudget] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  
  // Copy dialog
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyToMonth, setCopyToMonth] = useState((month % 12) + 1);
  const [copyToYear, setCopyToYear] = useState(month === 12 ? year + 1 : year);

  useEffect(() => {
    loadData();
  }, [month, year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [budgetRes, catRes] = await Promise.all([
        getBudgets(month, year),
        getCategories()
      ]);
      setBudgets(budgetRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  const handleSaveBudget = async (categoryId, amount) => {
    try {
      await createOrUpdateBudget({
        category_id: categoryId,
        month,
        year,
        amount: parseFloat(amount) || 0
      });
      toast.success('Budget salvato');
      setEditingBudget(null);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleCopyBudgets = async () => {
    try {
      await copyBudgets(month, year, copyToMonth, copyToYear);
      toast.success(`Budget copiati a ${MONTHS[copyToMonth - 1]} ${copyToYear}`);
      setCopyDialogOpen(false);
    } catch (error) {
      toast.error('Errore nella copia');
    }
  };

  // Group budgets by type with categories
  const getBudgetsByType = (type) => {
    const typeCats = categories.filter(c => c.type === type);
    return typeCats.map(cat => {
      const budget = budgets.find(b => b.category_id === cat.id);
      return {
        category_id: cat.id,
        category_name: cat.name,
        category_type: type,
        amount: budget?.amount || 0,
        actual: budget?.actual || 0
      };
    });
  };

  const calculateTotals = (type) => {
    const items = getBudgetsByType(type);
    return {
      budget: items.reduce((sum, i) => sum + i.amount, 0),
      actual: items.reduce((sum, i) => sum + i.actual, 0)
    };
  };

  const renderBudgetCard = (item) => {
    const percentage = item.amount > 0 ? Math.min((item.actual / item.amount) * 100, 100) : 0;
    const isOver = item.actual > item.amount && item.amount > 0;
    const isEditing = editingBudget === item.category_id;

    return (
      <div key={item.category_id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`budget-item-${item.category_id}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">{item.category_name}</span>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                className="w-24 h-8"
                autoFocus
                data-testid="budget-edit-input"
              />
              <Button 
                size="sm" 
                onClick={() => handleSaveBudget(item.category_id, editAmount)}
                data-testid="budget-save-btn"
              >
                <Save className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setEditingBudget(item.category_id); setEditAmount(item.amount.toString()); }}
              data-testid={`budget-edit-${item.category_id}`}
            >
              <Edit className="w-3 h-3 mr-1" /> {formatCurrency(item.amount)}
            </Button>
          )}
        </div>
        <div className="space-y-1">
          <Progress 
            value={percentage} 
            className={`h-2 ${isOver ? '[&>div]:bg-destructive' : ''}`}
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Effettivo: {formatCurrency(item.actual)}</span>
            {item.amount > 0 && (
              <span className={isOver ? 'text-destructive' : ''}>
                {isOver ? `+${formatCurrency(item.actual - item.amount)} oltre` : `${formatCurrency(item.amount - item.actual)} rimanenti`}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in" data-testid="budget-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Budget</h1>
          <p className="text-muted-foreground">Pianifica le tue entrate e uscite mensili</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCopyDialogOpen(true)} data-testid="copy-budget-btn">
            <Copy className="w-4 h-4 mr-2" /> Copia Budget
          </Button>
        </div>
      </div>

      {/* Month Selector */}
      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="budget-prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="px-6 py-2 bg-card border rounded-lg min-w-[200px] text-center">
          <span className="text-xl font-semibold">{MONTHS[month - 1]} {year}</span>
        </div>
        <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="budget-next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['REDDITO', 'SPESE', 'RISPARMI', 'DEBITI'].map(type => {
          const totals = calculateTotals(type);
          return (
            <Card key={type} className={TYPE_COLORS[type]?.bg} data-testid={`budget-summary-${type}`}>
              <CardContent className="pt-4">
                <p className={`text-sm font-medium ${TYPE_COLORS[type]?.text}`}>{type}</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totals.actual)}</p>
                <p className="text-sm text-muted-foreground">
                  di {formatCurrency(totals.budget)} pianificati
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Budget Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="REDDITO" data-testid="tab-reddito">Reddito</TabsTrigger>
              <TabsTrigger value="SPESE" data-testid="tab-spese">Spese</TabsTrigger>
              <TabsTrigger value="RISPARMI" data-testid="tab-risparmi">Risparmi</TabsTrigger>
              <TabsTrigger value="DEBITI" data-testid="tab-debiti">Debiti</TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : (
              <>
                {['REDDITO', 'SPESE', 'RISPARMI', 'DEBITI'].map(type => (
                  <TabsContent key={type} value={type} className="space-y-3 mt-0">
                    {getBudgetsByType(type).length > 0 ? (
                      getBudgetsByType(type).map(renderBudgetCard)
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Nessuna categoria per {type.toLowerCase()}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Copy Budget Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent data-testid="copy-budget-dialog">
          <DialogHeader>
            <DialogTitle>Copia Budget</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Copia i budget di {MONTHS[month - 1]} {year} verso un altro mese
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mese destinazione</Label>
                <Select value={copyToMonth.toString()} onValueChange={(v) => setCopyToMonth(parseInt(v))}>
                  <SelectTrigger data-testid="copy-to-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anno destinazione</Label>
                <Select value={copyToYear.toString()} onValueChange={(v) => setCopyToYear(parseInt(v))}>
                  <SelectTrigger data-testid="copy-to-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[year - 1, year, year + 1].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleCopyBudgets} data-testid="confirm-copy-btn">Copia Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BudgetPage;
